import classcat from "classcat";
import React from "react";

import BasketButton from "@is/components/BasketButton";
import EnlargeModal from "@is/components/EnlargeModal";
import ErrorPage from "@is/components/ErrorPage";
import ExternalPageDrawer from "@is/components/ExternalPageDrawer";
import FailedCheckoutModal from "@is/components/FailedCheckoutModal";
import Footer from "@is/components/Footer";
import GoToBottomButton from "@is/components/GoToBottomButton";
import LanguageAndCurrencyModal from "@is/components/LanguageAndCurrencyModal";
import QRCodeModal from "@is/components/QRCodeModal";
import SearchButton from "@is/components/SearchButton";
import SignUpModal from "@is/components/SignUpModal";
import SignUpStatusModal from "@is/components/SignUpStatusModal";
import StoreFavoritesModal from "@is/components/StoreFavoritesModal";
import StyleNotExistModal from "@is/features/Activities/StyleNotExistModal";
import BasketDrawer from "@is/features/BasketDrawer";
import CheckoutDrawer from "@is/features/CheckoutDrawer";
import ContentSection from "@is/features/ContentView/ContentSection";
import NewSearchDrawer from "@is/features/NewSearchDrawer";
import SettingsDrawer from "@is/features/SettingsDrawer";

import css from "./ContentView.module.css";
import PendingSpinner from "./PendingSpinner";
import useManageContentView from "./hooks/useManageContentView";

const ContentView = () => {
  const {
    isPortraitView,
    isLandscapeView,
    contentRef,
    searchDrawerRef,
    contentItemsRefs,
    contentItemsAnchorRefs,
    searchDrawerHeight,
    setSearchDrawerHeight,
    handleTouchMove,
    topSpace,
    handleSearchProducts,
    isSearchPending,
    errorSearchingProducts,
    sections,
    showBasketDrawer,
    handleOpenBasketDrawer,
    handleCloseBasketDrawer,
    basketItemsQuantity,
    showCheckoutDrawer,
    handleGoToCheckout,
    handleCloseCheckoutDrawer,
    handleEditCheckoutQuantity,
    checkoutConfigurationsType,
    isCheckoutEnabled,
    showQRCodeModal,
    qrCodeCheckout,
    handleQrCodeModalClose,
    handleCheckoutFailedModalClose,
    showLanguageAndCurrencyModal,
    handleToggleLanguageAndCurrencyModal,
    handleScrollToTheBottom,
    setShowGoToBottomButton,
    showGoToBottomButton,
    entryPageRoute,
    showSearchAndFooterDrawers,
    handleShowDrawers,
    showEnlargedModal,
    handleEnlargedModalClose,
    showSettingsDrawer,
    handleSettingDrawerClose,
    handleSettingDrawerOpen,
    handleStoreFavoritesModalOpen,
    showStoreFavoritesModal,
    handleStoreFavoritesModalClose,
    showStoreFavoritesButton,
    handleToggleSignUpModal,
    showSignUpModal,
    signUpStatusModal,
    handleSignUpStatusModalClose,
    t,
    handleCloseModal,
    showStyleNotExistModal,
    externalPageDrawer,
    handleExternalPageDrawerClose,
  } = useManageContentView();

  return (
    <div className={css.container}>
      {isLandscapeView && (
        <div className={css.landscapeSearchContainer}>
          <NewSearchDrawer
            isOpen
            innerRef={searchDrawerRef}
            onSearchDrawerHeightSet={setSearchDrawerHeight}
            handleSearch={handleSearchProducts}
          />
          <Footer
            isOpen
            isMinimized
            inDrawer={false}
            enableOverlay={false}
            showStoreFavoritesButton={showStoreFavoritesButton}
            onLogoClick={handleSettingDrawerOpen}
            onStoreFavoritesClick={handleStoreFavoritesModalOpen}
          />
        </div>
      )}

      <div
        className={isLandscapeView ? css.landscapeContentContainer : undefined}
      >
        {isPortraitView && (
          <>
            <NewSearchDrawer
              isOpen={showSearchAndFooterDrawers}
              inDrawer
              innerRef={searchDrawerRef}
              onSearchDrawerHeightSet={setSearchDrawerHeight}
              handleSearch={handleSearchProducts}
            />
            <Footer
              isOpen={showSearchAndFooterDrawers}
              inDrawer
              enableOverlay={false}
              showStoreFavoritesButton={showStoreFavoritesButton}
              onLogoClick={handleSettingDrawerOpen}
              onStoreFavoritesClick={handleStoreFavoritesModalOpen}
            />
          </>
        )}

        <div
          className={classcat([
            css.searchContainer,
            css.searchContainerTransition,
          ])}
          style={{
            top: topSpace,
          }}
        >
          {isPortraitView && (
            <div className={css.searchButtonContainer}>
              <SearchButton
                onClick={handleShowDrawers}
                isVisible={isPortraitView && !showSearchAndFooterDrawers}
              />
            </div>
          )}
          <BasketButton
            count={basketItemsQuantity}
            onClick={handleOpenBasketDrawer}
            hidden={!isCheckoutEnabled}
          />
        </div>
        <div className={css.goToBottomButtonContainer}>
          <GoToBottomButton
            onClick={handleScrollToTheBottom}
            isVisible={showGoToBottomButton}
          />
        </div>
        <div
          className={css.contentItemsContainer}
          ref={contentRef}
          onTouchMove={handleTouchMove}
          style={{
            width: isPortraitView ? "100vw" : "100%",
          }}
        >
          {sections.map((section) => (
            <ContentSection
              contentItemsRefs={contentItemsRefs}
              contentItemsAnchorRefs={contentItemsAnchorRefs}
              searchDrawerHeight={isPortraitView ? searchDrawerHeight : 0}
              key={section.id}
              section={section}
              setShowGoToBottomButton={setShowGoToBottomButton}
            />
          ))}
          <PendingSpinner
            show={isSearchPending}
            offset={isPortraitView ? searchDrawerHeight : 0}
          />
          {errorSearchingProducts && <ErrorPage t={t} url={entryPageRoute} />}
        </div>

        <BasketDrawer
          isOpen={showBasketDrawer}
          onGoToCheckout={handleGoToCheckout}
          onClose={handleCloseBasketDrawer}
        />
        <CheckoutDrawer
          isOpen={showCheckoutDrawer}
          onClose={handleCloseCheckoutDrawer}
          onEditQuantity={handleEditCheckoutQuantity}
          checkoutConfigurationsType={checkoutConfigurationsType}
        />
        <SettingsDrawer
          isOpen={showSettingsDrawer}
          onClose={handleSettingDrawerClose}
        />
        {showEnlargedModal && (
          <EnlargeModal
            isOpen={showEnlargedModal}
            onClose={handleEnlargedModalClose}
          />
        )}
        {showLanguageAndCurrencyModal && (
          <LanguageAndCurrencyModal
            isOpen={true}
            onClose={handleToggleLanguageAndCurrencyModal}
          />
        )}
        {showStyleNotExistModal && (
          <StyleNotExistModal onClose={handleCloseModal} />
        )}
        {showQRCodeModal && (
          <QRCodeModal isOpen={true} onClose={handleQrCodeModalClose} />
        )}
        {qrCodeCheckout.isFailed && (
          <FailedCheckoutModal
            isOpen={true}
            onClose={handleCheckoutFailedModalClose}
          />
        )}
        {showStoreFavoritesModal && (
          <StoreFavoritesModal
            isOpen
            onClose={handleStoreFavoritesModalClose}
          />
        )}
        {showSignUpModal && (
          <SignUpModal isOpen onClose={handleToggleSignUpModal} />
        )}
        {signUpStatusModal.isOpen && (
          <SignUpStatusModal
            isOpen
            onClose={handleSignUpStatusModalClose}
            status={signUpStatusModal.status}
            errorMessage={signUpStatusModal.errorMessage}
          />
        )}
        {externalPageDrawer.isOpen && (
          <ExternalPageDrawer
            handleClose={handleExternalPageDrawerClose}
            src={externalPageDrawer.src}
          />
        )}
      </div>
    </div>
  );
};

export default ContentView;
